"use client";

import React, { useEffect, useState, useMemo } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import OTCHistoryDetailsView from "../../MedicalHistory/views/scriptHistoryDetails";
import PatientSignatureModal from "../../../global/view/patientSignature";
import DoctorePrescription from "../../../global/view/doctorePrescription";
import ClaimStatus from "../../../global/view/ClaimStatus";
import { useAuth } from "../../../../context/AuthContext";
import { createProcessPrescription } from "../../../../api/dispensary/script/processScriptApi";

export default function PriceScriptPaymentSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const { scriptData, continueAfterMedicalAid, scriptFile } = location.state || {};

  const { user, token } = useAuth();

  const [showPatientSig, setShowPatientSig] = useState(false);
  const [pharmacistSig, setPharmacistSig] = useState(null);
  const [patientSig, setPatientSig] = useState(null);
  const [showDoctorModal, setShowDoctorModal] = useState(false);
  const [showClaimStatus, setShowClaimStatus] = useState(false);
  const [progressPercent, setProgressPercent] = useState(70);
  const [doctorPreview, setDoctorPreview] = useState(null);
  const [paymentMeta, setPaymentMeta] = useState(null);
  const [localScriptUrl, setLocalScriptUrl] = useState(null);

  const isMedicalAid = (scriptData?.paymentType || "").toLowerCase() === "medical aid";

  const todayIsoDate = useMemo(() => {
    try {
      return new Date().toISOString().split("T")[0];
    } catch {
      return undefined;
    }
  }, []);

  // Create a local object URL for the uploaded script file (if any) so that
  // the embedded history/details view can immediately open the script the
  // user selected on the previous page.
  useEffect(() => {
    if (!scriptFile) {
      setLocalScriptUrl(null);
      return;
    }
    const url = URL.createObjectURL(scriptFile);
    setLocalScriptUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore revoke errors
      }
    };
  }, [scriptFile]);

  const scriptDataForView = useMemo(
    () => ({
      ...(scriptData || {}),
      scriptDocUrl: localScriptUrl || scriptData?.scriptDocUrl || scriptData?.docurl || null,
    }),
    [scriptData, localScriptUrl]
  );

  const getUserIdFromToken = () => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.userid || null;
    } catch {
      return null;
    }
  };

  // Shared totals for the price-script flow. Prefer the grossAmount computed
  // in OTCHistoryDetailsView (paymentMeta.grossAmount) and only fall back to
  // scriptData.total. Amount/VAT use scriptData when available.
  const totals = {
    amount: scriptData?.amount ?? "",
    vat: scriptData?.vat ?? "",
    total: paymentMeta?.grossAmount
      ? `R ${paymentMeta.grossAmount}`
      : scriptData?.total
      ? `R${scriptData.total.toFixed?.(2) ?? scriptData.total}`
      : "",
  };

  const handleDoctorComplete = async () => {
    const sd = scriptData || {};
    const pharmacistid = getUserIdFromToken();

    const items = Array.isArray(sd.drugs)
      ? sd.drugs
          .filter((m) => m && m.sku && m.saved !== false)
          .map((m) => ({
            sku: m.sku,
            quantity: m.quantity,
            stockQty: m.stockQty,
            toFollowStruck: m.toFollowStruck,
            instructions: m.instructions,
            instructionsOther: m.instructionsOther,
            deliveryOption: m.deliveryOption,
          }))
      : [];

    const pm = paymentMeta || {};

    const payload = {
      profilenumber: sd.profileNo || null,
      patientid: sd.patientId || null,
      practitionerid: sd.practitionerId || null,
      pharmacistid: pharmacistid || null,
      paymenttype: sd.paymentType || null,
      chronic: !!sd.isChronic,
      repeatscript: sd.isRepeat ? (sd.repeatCount || 0) : 0,
      cashpricingoption: sd.scriptType || null,
      processscriptnumber: sd.scriptNumber || sd.scriptNo || null,
      branchcode: sd.branchcode || user?.branchcode || null,
      deliveryoption: pm.deliveryOption || "Collect",
      preferreddate: pm.preferredDate || null,
      preferredtime: pm.preferredTime || null,
      dispensingfee: pm.dispensingFee ?? null,
      gross: pm.grossAmount ?? null,
      items,
    };

    if (scriptFile) {
      payload.docurl = scriptFile;
    }

    const resp = await createProcessPrescription(payload, scriptFile);
    if (resp?.success === false) {
      throw new Error(resp?.message || "Failed to create prescription");
    }
    return resp?.data?.prescriptionid || null;
  };

  useEffect(() => {
    if (isMedicalAid && !continueAfterMedicalAid) {
      setProgressPercent(70);
      setShowClaimStatus(true);
    }
    const t = setInterval(() => setProgressPercent((p) => Math.min(100, p + 5)), 500);
    return () => clearInterval(t);
  }, [scriptData, showClaimStatus, progressPercent, isMedicalAid, navigate, location.pathname]);

  useEffect(() => {
    if (continueAfterMedicalAid) {
      if (location.state?.pharmacistSignature) {
        setPharmacistSig(location.state.pharmacistSignature);
      }
      setShowClaimStatus(false);
      setShowPatientSig(true);
    }
  }, [continueAfterMedicalAid, location.state]);

  return (
    <>
      <OTCHistoryDetailsView
        scriptData={scriptDataForView}

        showTotalItems={true}
        showPharmacistReadOnly={true}
        showPaymentTypeReadOnly={true}
        showPractitionerReadOnly={true}
        rowsPerPage={1000}
        invoiceActionLabel="Sign Invoice"
        minPreferredDate={todayIsoDate}
        // For price-script payment summary, we are previewing a new/draft
        // script. Avoid fetching remote script details by interpreting the
        // scriptNumber as a persisted prescription id.
        disableRemoteScriptDetails={true}

        onPreviewDataChange={(data) => {
          setDoctorPreview(data);
          if (data?.paymentMeta) {
            setPaymentMeta(data.paymentMeta);
          }
        }}
        onInvoiceSigned={(sig) => {
          setPharmacistSig(sig);
          if (isMedicalAid) {
            setProgressPercent(70);
            setShowClaimStatus(true);
          } else {
            setShowPatientSig(true);
          }
        }}
      />

      <PatientSignatureModal
        isOpen={showPatientSig}
        onClose={() => setShowPatientSig(false)}
        onConfirm={async (dataUrl) => {
          try {
            setPatientSig(dataUrl);
            await handleDoctorComplete();
            setShowPatientSig(false);
            setShowDoctorModal(true);
          } catch (e) {
            console.error("Failed to create prescription on patient confirm (Price Script):", e);
          }
        }}
        initialSignature={patientSig}
        totals={totals}
      />

      <DoctorePrescription
        isOpen={showDoctorModal}
        onClose={() => setShowDoctorModal(false)}
        patientInfo={doctorPreview?.patientInfo}
        prescription={doctorPreview?.prescription}
        pharmacistSignature={pharmacistSig}
        patientSignature={patientSig}
        totals={totals}
        useCompletedPopup={true}
      />

      {isMedicalAid && (
        <ClaimStatus
          isOpen={showClaimStatus}
          onClose={() => setShowClaimStatus(false)}
          scriptData={scriptData}
          claimDetails={{ progressPercent }}
        />
      )}
    </>
  );
}